import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, PageHeader, SectionLabel } from "@/components/Reveal";
import { APPROACH_STEPS, DIFFERENTIATORS, ASSET_CLASSES, INDUSTRIES, IMAGES } from "@/data/content";

const Steps = () => (
  <section className="py-24 px-6 sm:px-12" data-testid="approach-steps">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionLabel>The Method</SectionLabel>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink max-w-2xl">
          A structured, research-driven approach.
        </h2>
      </Reveal>
      <div className="mt-16 border-t border-border">
        {APPROACH_STEPS.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.05}>
            <div
              className="grid grid-cols-12 gap-6 py-10 border-b border-border items-baseline group hover:bg-white transition-colors duration-300 px-2 sm:px-6"
              data-testid={`approach-step-${i}`}
            >
              <span className="col-span-3 sm:col-span-2 font-serif text-5xl sm:text-7xl text-gold/30 group-hover:text-gold transition-colors duration-500">
                {s.num}
              </span>
              <h3 className="col-span-9 sm:col-span-3 font-serif text-2xl sm:text-3xl text-ink">{s.title}</h3>
              <p className="col-span-12 sm:col-span-7 text-base leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ParallaxImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} className="relative h-[60vh] overflow-hidden" data-testid="approach-parallax">
      <motion.img
        src={IMAGES.blueprints}
        alt="Research and analysis at Auric Valuation"
        style={{ y }}
        className="absolute inset-0 w-full h-[124%] object-cover"
      />
      <div className="absolute inset-0 bg-ink/60 flex items-center px-6 sm:px-12">
        <div className="max-w-7xl mx-auto w-full">
          <Reveal>
            <p className="font-serif text-cream text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-3xl">
              "We don't just value assets, we help you realize their true potential."
            </p>
            <p className="mt-6 text-xs tracking-[0.3em] uppercase font-semibold text-gold-light">
              Value Beyond Numbers
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Differentiators = () => (
  <section className="py-24 px-6 sm:px-12 bg-white border-y border-border" data-testid="differentiators-section">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionLabel>Key Differentiators</SectionLabel>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink">Why institutions rely on us.</h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
        {DIFFERENTIATORS.map((d, i) => (
          <Reveal key={d.title} delay={(i % 3) * 0.08} className="bg-white">
            <div className="p-10 hover:-translate-y-1 transition-transform duration-300 h-full" data-testid={`differentiator-card-${i}`}>
              <span className="block w-8 h-[2px] bg-gold" />
              <h3 className="mt-6 font-serif text-xl text-ink">{d.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">{d.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AssetClasses = () => (
  <section className="py-24 px-6 sm:px-12" data-testid="asset-classes-section">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Reveal>
          <SectionLabel>Valuation Expertise</SectionLabel>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink">
            Asset classes we value.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-600">
            Financial acumen, market intelligence and technical expertise — combined to deliver
            well-supported valuation opinions across asset classes and industry sectors.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border self-start">
        {ASSET_CLASSES.map((a, i) => (
          <Reveal key={a.title} delay={(i % 2) * 0.08} className="bg-white">
            <div className="p-8 hover:bg-cream transition-colors duration-300 h-full" data-testid={`asset-card-${i}`}>
              <h3 className="font-serif text-lg text-ink">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{a.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-ink text-cream py-24 px-6 sm:px-12" data-testid="industries-section">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <Reveal>
          <SectionLabel light>Industry Sectors</SectionLabel>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight">Sectors we serve.</h2>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <ul className="divide-y divide-white/10 border-y border-white/10">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind} delay={i * 0.05} y={12}>
              <li className="py-5 flex items-baseline gap-6 group" data-testid={`industry-${i}`}>
                <span className="text-xs text-gold-light font-semibold">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-xl sm:text-2xl text-slate-200 group-hover:text-gold-light transition-colors duration-300">
                  {ind}
                </span>
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.3}>
          <p className="mt-8 text-sm text-slate-400">…and many more.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Standards = () => (
  <section className="py-24 px-6 sm:px-12" data-testid="standards-section">
    <div className="max-w-4xl mx-auto">
      <Reveal>
        <SectionLabel>Valuation Standards & Compliance</SectionLabel>
        <ul className="mt-10 space-y-6">
          {[
            "We adhere to globally accepted valuation standards including IVS (International Valuation Standards) issued by IVSC.",
            "Our valuations are prepared in compliance with IBBI Valuation Standards, Companies Act, SEBI, RBI, IRDAI and other applicable regulatory requirements.",
            "We ensure the highest levels of ethics, confidentiality, transparency and professional integrity in every engagement.",
          ].map((t, i) => (
            <li key={i} className="flex gap-5 items-baseline" data-testid={`standard-${i}`}>
              <span className="w-6 h-[2px] bg-gold shrink-0 translate-y-[-4px]" />
              <p className="text-base leading-relaxed text-slate-600">{t}</p>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

export default function Approach() {
  return (
    <>
      <PageHeader
        label="Our Approach"
        lines={["Credible. Independent.", "Defensible."]}
        sub="A structured and research-driven approach to deliver valuation reports that empower better business decisions."
      />
      <Steps />
      <ParallaxImage />
      <Differentiators />
      <AssetClasses />
      <Industries />
      <Standards />
    </>
  );
}
